import { DEFAULT_PAGE_INFO, SortInput } from '@apeiron/common'
import { StarFilterInput, StarLite, StarSortBy } from '@apeiron/star'
import {
  deserializeListData,
  deserializePageInfo,
  PageInfo,
  PageInput,
  serializePagingInput
} from '@apeiron/temp'
import { useLazyQuery } from '@apollo/client'
import { starLiteSchema } from '@src/deserialize/yup/star'
import graphql from '@src/graphql/query/getStars'
import { destructConstellationUniqueKey } from '@src/util/apeiron/star'
import * as R from 'ramda'
import { useCallback, useState } from 'react'

const useGetStars = (): Hook => {
  const [query, { loading }] = useLazyQuery(graphql)

  const [data, setData] = useState<StarLite[]>([])

  const [pageInfo, setPageInfo] = useState<PageInfo>(DEFAULT_PAGE_INFO)

  const fetch = useCallback(
    async (request: Request): Promise<Response> => {
      const {
        filterInput = {},
        pageInput = {},
        sortInput = { sortBy: StarSortBy.StarIdAsc }
      } = request

      const { constellationComposes = [], ...rest } = filterInput

      const composeParams = R.map(compose => {
        return destructConstellationUniqueKey(compose)
      }, constellationComposes)

      const response = await query({
        variables: {
          filterInput: {
            ...rest,
            ...(!R.isEmpty(composeParams)
              ? { constellationComposes: composeParams }
              : {})
          },
          pagingInput: serializePagingInput(pageInput),
          sortInput
        }
      })

      const stars = deserializeListData<StarLite>(response, starLiteSchema)

      const pageInfo = deserializePageInfo(response, pageInput)

      setData(stars)

      setPageInfo(pageInfo)

      return {
        data: stars,
        pageInfo
      }
    },
    [query]
  )

  return {
    data,
    fetch,
    loading,
    pageInfo
  }
}

type Request = {
  filterInput?: StarFilterInput
  pageInput?: PageInput
  sortInput?: SortInput<StarSortBy>
}

type Response = {
  data: StarLite[]
  pageInfo: PageInfo
}

type Hook = {
  data: StarLite[]
  fetch: (request: Request) => Promise<Response>
  loading: boolean
  pageInfo: PageInfo
}

export default useGetStars
